import { useState, useEffect, useMemo } from 'react'
import { Button } from '@/components/ui/button'
import { useToast } from '@/hooks/use-toast'
import { Loader2, Upload, FileText, User, ChevronRight, Star, ChevronLeft, ChevronsRight, ChevronDown, ChevronUp, ArrowRight, CheckCircle2, History } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
} from '@/components/ui/dialog'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { useDropzone } from 'react-dropzone'
import Papa from 'papaparse'
import { parse as parseVCard } from 'vcard-parser'
import { useWorkspaces } from '@/hooks/useWorkspaces'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { ImportGroupSelector } from '@/components/contacts/import/ImportGroupSelector'
import { Progress } from '@/components/ui/progress'
import { format } from 'date-fns'
import { detectCrmSchema, parseCrmCsvRow, transformCrmContactToImport, decodeUnicodeEscapes } from '@/lib/crm-import-utils'
import { supabase } from '@/lib/supabase'
import { captureError } from '@/lib/sentry'

const contactFields = [
  { value: 'first_name', label: 'First Name' },
  { value: 'last_name', label: 'Last Name' },
  { value: 'job_title', label: 'Job Title' },
  { value: 'company', label: 'Company' },
  { value: 'website', label: 'Website' },
  { value: 'emails', label: 'Email Addresses' },
  { value: 'phones', label: 'Phone Numbers' },
  { value: 'addresses', label: 'Full Address (single field)' },
  // Address component fields for split address columns
  { value: 'address_street', label: 'Address - Street Line 1' },
  { value: 'address_street2', label: 'Address - Street Line 2' },
  { value: 'address_city', label: 'Address - City' },
  { value: 'address_state', label: 'Address - State/Province' },
  { value: 'address_zip', label: 'Address - Zip/Postal Code' },
  { value: 'address_country', label: 'Address - Country' },
  // Second address support
  { value: 'address2_street', label: 'Address 2 - Street Line 1' },
  { value: 'address2_street2', label: 'Address 2 - Street Line 2' },
  { value: 'address2_city', label: 'Address 2 - City' },
  { value: 'address2_state', label: 'Address 2 - State/Province' },
  { value: 'address2_zip', label: 'Address 2 - Zip/Postal Code' },
  { value: 'address2_country', label: 'Address 2 - Country' },
  { value: 'dates', label: 'Important Dates' },
  { value: 'notes', label: 'Notes' },
  { value: 'type', label: 'Contact Type' },
  { value: 'rank', label: 'Priority Rank' },
  { value: 'status', label: 'Outreach Status' },
  { value: 'relationship', label: 'Relationship Type' },
  { value: 'industry', label: 'Industry' },
  { value: 'timezone', label: 'Timezone' },
  { value: 'remarketing', label: 'Allow Marketing' },
  { value: 'entity_type', label: 'Entity Type (Person/Company)' },
  { value: 'groups', label: 'Groups' },
  { value: 'custom_fields', label: 'Custom Fields' },
  { value: 'source_contact_id', label: 'Source Contact ID' },
]

// Enhanced field options with specific labels
const emailLabels = [
  { value: 'work', label: 'Work Email' },
  { value: 'personal', label: 'Personal Email' },
  { value: 'assistant', label: 'Assistant Email' },
  { value: 'additional', label: 'Additional Email' },
]

const phoneLabels = [
  { value: 'main', label: 'Main Phone' },
  { value: 'business', label: 'Business Phone' },
  { value: 'assistant', label: 'Assistant Phone' },
  { value: 'fax', label: 'Fax Number' },
  { value: 'mobile', label: 'Mobile Number' },
  { value: 'home', label: 'Home Phone' },
  { value: 'additional', label: 'Additional Phone' },
]

const addressLabels = [
  { value: 'main', label: 'Main Address' },
  { value: 'home', label: 'Home Address' },
  { value: 'business', label: 'Business Address' },
  { value: 'work', label: 'Work Address' },
  { value: 'additional', label: 'Additional Address' },
]

const dateLabels = [
  { value: 'birthday', label: 'Birthday' },
  { value: 'anniversary', label: 'Anniversary' },
  { value: 'additional', label: 'Additional Date' },
]

// A .csv can arrive typed application/vnd.ms-excel (Windows + Excel), octet-stream, or empty.
// Route by extension OR MIME so every CSV takes the CSV path.
function isCsvFile(file: File): boolean {
  return file.type === 'text/csv' || file.name.toLowerCase().endsWith('.csv')
}

// Label from CSV header words, using the same label values extractVCardLabel emits.
// First matching word wins; null = no label word (caller keeps the position default).
const headerLabelWords: { [field: string]: [string[], string][] } = {
  emails: [[['work', 'business', 'office'], 'work'], [['personal', 'home'], 'personal'], [['assistant'], 'assistant']],
  phones: [[['fax'], 'fax'], [['mobile', 'cell'], 'mobile'], [['work', 'business', 'office'], 'business'], [['home'], 'home'], [['main'], 'main'], [['assistant'], 'assistant']],
  dates: [[['birthday', 'birth', 'dob', 'birthdate'], 'birthday'], [['anniversary'], 'anniversary']],
}
// contact_dates.date_value is a Postgres date; one bad value fails the whole server batch insert.
// Accept only real ISO YYYY-MM-DD or US M/D/YYYY (normalized to ISO); anything else -> null (skip).
function normalizeCsvDate(raw: string): string | null {
  const v = raw.trim()
  const iso = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v)
  const us = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(v)
  const [y, m, d] = iso ? [+iso[1], +iso[2], +iso[3]] : us ? [+us[3], +us[1], +us[2]] : [0, 0, 0]
  if (y < 1) return null
  const dt = new Date(Date.UTC(y, m - 1, d))
  dt.setUTCFullYear(y) // Date.UTC maps years 0-99 to 1900s
  if (dt.getUTCFullYear() !== y || dt.getUTCMonth() !== m - 1 || dt.getUTCDate() !== d) return null
  return `${String(y).padStart(4, '0')}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`
}

function labelFromHeader(normalizedHeader: string, field: string): string | null {
  const words = normalizedHeader.split(' ')
  for (const [keys, label] of headerLabelWords[field] || []) {
    if (keys.some(k => words.includes(k))) return label
  }
  return null
}

// Extract meaningful label + primary status from vCard meta.type arrays
function extractVCardLabel(
  meta: { [key: string]: string[] } | undefined,
  fieldType: 'emails' | 'phones' | 'addresses'
): { label: string; isPrimary: boolean } {
  const types = meta?.type || []
  const upperTypes = types.map(t => t.toUpperCase())

  const isPrimary = upperTypes.includes('PREF')

  // Filter out noise types that don't convey semantic meaning
  const noiseTypes = new Set(['INTERNET', 'VOICE', 'PREF', 'TEXT', 'TEXTPHONE', 'VIDEO', 'MSG'])
  const meaningfulTypes = upperTypes.filter(t => !noiseTypes.has(t))

  // Map vCard types to app labels
  const typeToLabel: { [key: string]: string } = {
    HOME: fieldType === 'emails' ? 'personal' : 'home',
    WORK: fieldType === 'phones' ? 'business' : 'work',
    CELL: 'mobile',
    FAX: 'fax',
    MAIN: 'main',
  }

  for (const t of meaningfulTypes) {
    if (typeToLabel[t]) {
      return { label: typeToLabel[t], isPrimary }
    }
  }

  return { label: 'additional', isPrimary }
}

// Separate component for the preview table with scroll indicators
function PreviewTable({
  columnMap,
  previewData,
  emailLabels: emailLabelsList,
  phoneLabels: phoneLabelsList,
  addressLabels: addressLabelsList,
  dateLabels: dateLabelsList,
  onBack,
  onNext,
}: {
  columnMap: { [key: string]: { field: string; label?: string; isPrimary?: boolean } }
  previewData: any[]
  emailLabels: { value: string; label: string }[]
  phoneLabels: { value: string; label: string }[]
  addressLabels: { value: string; label: string }[]
  dateLabels: { value: string; label: string }[]
  onBack: () => void
  onNext: () => void
}) {
  const [scrollState, setScrollState] = useState({ isAtStart: true, isAtEnd: false })

  // Count visible columns (mapped and not ignored)
  const visibleColumns = useMemo(() => {
    return Object.entries(columnMap).filter(([, mapping]) =>
      mapping.field && mapping.field !== 'ignore'
    )
  }, [columnMap])

  const totalColumns = visibleColumns.length

  // Handle scroll events to update scroll state
  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.target as HTMLDivElement
    const isAtStart = target.scrollLeft <= 10
    const isAtEnd = target.scrollLeft + target.clientWidth >= target.scrollWidth - 10
    setScrollState({ isAtStart, isAtEnd })
  }

  return (
    <div className="flex flex-col h-full max-h-[70vh] w-full">
      <div className="flex-shrink-0 pb-4">
        <h3 className="text-lg font-medium">Preview Data</h3>
        <p className="text-sm text-muted-foreground">
          Preview how your data will be imported. Showing first {previewData.length} rows with your applied mappings.
        </p>
        {totalColumns > 4 && (
          <div className="flex items-center gap-2 mt-2 text-sm text-blue-600 dark:text-blue-400">
            <ChevronsRight className="h-4 w-4 animate-pulse" />
            <span>Scroll horizontally to see all {totalColumns} columns</span>
          </div>
        )}
      </div>

      <div className="flex-1 mb-4 min-w-0 w-full">
        <div className="border rounded-md h-80 bg-background relative">
          {/* Left scroll shadow indicator */}
          <div
            className={`absolute left-0 top-0 bottom-0 w-8 bg-gradient-to-r from-background/80 to-transparent z-20 pointer-events-none transition-opacity duration-200 ${scrollState.isAtStart ? 'opacity-0' : 'opacity-100'}`}
          >
            <div className="absolute left-2 top-1/2 -translate-y-1/2">
              <ChevronLeft className="h-5 w-5 text-muted-foreground" />
            </div>
          </div>

          {/* Right scroll shadow indicator */}
          <div
            className={`absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-background/80 to-transparent z-20 pointer-events-none transition-opacity duration-200 ${scrollState.isAtEnd ? 'opacity-0' : 'opacity-100'}`}
          >
            <div className="absolute right-2 top-1/2 -translate-y-1/2">
              <ChevronRight className="h-5 w-5 text-muted-foreground" />
            </div>
          </div>

          {/* Scrollable container - always show scrollbar */}
          <div
            className="absolute inset-0 overflow-x-scroll overflow-y-auto"
            onScroll={handleScroll}
            style={{
              // Force scrollbar to always be visible
              scrollbarWidth: 'auto',
              scrollbarColor: 'hsl(var(--border)) transparent',
            }}
          >
            <table className="border-collapse" style={{ minWidth: 'max-content' }}>
              {/* Header */}
              <thead className="bg-muted sticky top-0 z-10">
                <tr>
                  {visibleColumns.map(([column, mapping]) => (
                    <th key={column} className="px-3 py-2 text-left text-sm font-medium border-r border-border whitespace-nowrap bg-muted" style={{ minWidth: '140px' }}>
                      <div className="font-semibold">
                        {column === '__emails__' ? 'Email Addresses' :
                         column === '__phones__' ? 'Phone Numbers' :
                         column === '__addresses__' ? 'Addresses' :
                         column === '__birthday__' ? 'Birthday' :
                         mapping.field === 'first_name' ? 'First Name' :
                         mapping.field === 'last_name' ? 'Last Name' :
                         mapping.field === 'job_title' ? 'Job Title' :
                         mapping.field === 'emails' ? (mapping.label ?
                           emailLabelsList.find(l => l.value === mapping.label)?.label || 'Email' : 'Email') :
                         mapping.field === 'phones' ? (mapping.label ?
                           phoneLabelsList.find(l => l.value === mapping.label)?.label || 'Phone' : 'Phone') :
                         mapping.field === 'addresses' ? (mapping.label ?
                           addressLabelsList.find(l => l.value === mapping.label)?.label || 'Address' : 'Address') :
                         mapping.field === 'dates' ? (mapping.label ?
                           dateLabelsList.find(l => l.value === mapping.label)?.label || 'Important Dates' : 'Important Dates') :
                         mapping.field === 'company' ? 'Company' :
                         mapping.field === 'website' ? 'Website' :
                         mapping.field === 'notes' ? 'Notes' :
                         mapping.field}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        from: {column.startsWith('__') ? 'vCard' : column}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>

              {/* Data rows */}
              <tbody>
                {previewData.map((row, rowIndex) => (
                  <tr key={rowIndex} className={`border-b border-border ${rowIndex % 2 === 0 ? 'bg-background' : 'bg-muted/20'}`}>
                    {visibleColumns.map(([column]) => (
                      <td key={`${rowIndex}-${column}`} className="px-3 py-2 text-sm border-r border-border whitespace-nowrap" style={{ minWidth: '140px' }}>
                        <div className="max-w-[180px] truncate" title={row[column] || ''}>
                          {row[column] || '-'}
                        </div>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="flex-shrink-0 flex justify-end gap-3 pt-4 border-t">
        <Button variant="outline" onClick={onBack}>Back to Mapping</Button>
        <Button onClick={onNext}>Next: Import</Button>
      </div>
    </div>
  )
}

/**
 * Props for ImportContactsDialog.
 *
 * `workspaceIdOverride` and `adminContext` are PR-4 additions for the
 * super-admin "Import on behalf of user" flow. Default undefined preserves
 * the regular user-facing behavior — regular users always import into their
 * current workspace from `useWorkspaces()`.
 */
interface ImportContactsDialogProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /**
   * When set, overrides the regular `currentWorkspace` for the import target.
   * Used by the super-admin "Import on behalf of user" flow. Default undefined
   * preserves the current user-facing behavior.
   */
  workspaceIdOverride?: string;
  /**
   * When in admin mode, optionally show a banner indicating who the import
   * is on behalf of and into which workspace. The presence of this prop also
   * switches the default group-name format to the admin format.
   */
  adminContext?: {
    targetUserName: string;
    targetWorkspaceName: string;
  };
}

export function ImportContactsDialog({
  open = false,
  onOpenChange = () => {},
  workspaceIdOverride,
  adminContext,
}: ImportContactsDialogProps) {
  const [activeTab, setActiveTab] = useState('upload')
  const [isOpen, setIsOpen] = useState(open)
  const [file, setFile] = useState<File | null>(null)
  const [columns, setColumns] = useState<string[]>([])
  const [columnMap, setColumnMap] = useState<{ [key: string]: { field: string; label?: string; isPrimary?: boolean } }>({})
  const [previewData, setPreviewData] = useState<any[]>([])
  const [csvData, setCSVData] = useState<any[]>([])
  const [vcfData, setVcfData] = useState<any[]>([])
  const [fileContent, setFileContent] = useState<string>('')
  const [loading, setLoading] = useState(false)
  const [groupOption, setGroupOption] = useState<{
    mode: 'none' | 'new' | 'existing';
    groupId?: string;
    groupName?: string;
  }>({ mode: 'none' })
  // T5: State for tracking which rows have expanded advanced options
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set())
  // Track if CRM schema was detected in the CSV
  const [isCrmSchema, setIsCrmSchema] = useState(false)
  // Track CRM field counts for the import summary
  const [crmFieldCounts, setCrmFieldCounts] = useState<Record<string, number>>({})
  // Track import submission status for showing confirmation screen
  const [importStatus, setImportStatus] = useState<'idle' | 'submitting' | 'submitted'>('idle')
  const [importedContactCount, setImportedContactCount] = useState(0)
  // Chunked-import progress: rows actually written to DB so far across chunks.
  // Distinct from importedContactCount which is the final post-success total.
  const [importProgress, setImportProgress] = useState(0)
  // Marketing consent: require an affirmation when an "Allow Marketing" column
  // is mapped (the YES rows are logged server-side on import). See #447.
  const [importConsentAffirmed, setImportConsentAffirmed] = useState(false)
  const remarketingMapped = useMemo(
    () => Object.values(columnMap).some((m) => m?.field === 'remarketing'),
    [columnMap],
  )
  const { toast } = useToast()
  const { currentWorkspace } = useWorkspaces()

  // PR-4: Resolve the import target. In admin mode the super-admin passes
  // `workspaceIdOverride` (the workspace they picked for the target user);
  // regular users fall back to their own current workspace. All downstream
  // workspace-scoped calls (group selector, edge fn payload, submit guard)
  // must use this resolved value, not currentWorkspace directly.
  const targetWorkspaceId = workspaceIdOverride ?? currentWorkspace?.id ?? null

  // Generate suggested group name based on current date. Admin mode uses a
  // distinct prefix to make the source obvious in the contacts UI later.
  const suggestedGroupName = useMemo(() => {
    if (adminContext) {
      return `Admin CSV Import — ${format(new Date(), 'yyyy-MM-dd HH:mm')}`;
    }
    return `Import - ${format(new Date(), 'yyyy-MM-dd')}`;
  }, [adminContext])

  // Define user-friendly names for VCard fields at component level
  const vcardFieldDisplayNames: { [key: string]: string } = {
    'email': 'Email Address',
    'tel': 'Phone Number',
    'adr': 'Address',
    'org': 'Company/Organization',
    'title': 'Job Title',
    'url': 'Website',
    'note': 'Notes',
    'bday': 'Birthday'
  }

  // vCard fields that are automatically handled (no manual mapping needed)
  // Names are extracted from fn/n; emails, phones, addresses, birthdays get per-entry labels from meta.type
  const autoMappedVCardFields = ['fn', 'n', 'email', 'tel', 'adr', 'bday']

  const resetState = () => {
    setActiveTab('upload')
    setFile(null)
    setColumns([])
    setColumnMap({})
    setPreviewData([])
    setCSVData([])
    setVcfData([])
    setFileContent('')
    setGroupOption({ mode: 'none' })
    setExpandedRows(new Set())
    setIsCrmSchema(false)
    setCrmFieldCounts({})
    setImportStatus('idle')
    setImportedContactCount(0)
  }

  // T6: Toggle expanded state for a row's advanced options
  const toggleRowExpanded = (column: string) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev)
      if (newSet.has(column)) {
        newSet.delete(column)
      } else {
        newSet.add(column)
      }
      return newSet
    })
  }

  // T6: Helper to get label display text for summary
  const getLabelDisplayText = (fieldType: string, label?: string, isPrimary?: boolean): string => {
    if (!label) return ''

    let labelText = ''
    if (fieldType === 'emails') {
      labelText = emailLabels.find(l => l.value === label)?.label || label
    } else if (fieldType === 'phones') {
      labelText = phoneLabels.find(l => l.value === label)?.label || label
    } else if (fieldType === 'addresses') {
      labelText = addressLabels.find(l => l.value === label)?.label || label
    } else if (fieldType === 'dates') {
      labelText = dateLabels.find(l => l.value === label)?.label || label
    }

    // Remove field type from label text (e.g., "Work Email" -> "Work")
    labelText = labelText.replace(/\s*(Email|Phone|Address|Date)$/i, '')

    if (isPrimary) {
      return `${labelText}, Primary`
    }
    return labelText
  }

  // Define mapping patterns for common CSV column names
  const commonColumnMappings: { [key: string]: string } = {
    // First name mappings
    'first name': 'first_name',
    'firstname': 'first_name',
    'fname': 'first_name',
    'given name': 'first_name',
    'givenname': 'first_name',
    
    // Last name mappings
    'last name': 'last_name',
    'lastname': 'last_name',
    'lname': 'last_name',
    'surname': 'last_name',
    'family name': 'last_name',
    'familyname': 'last_name',
    
    // Email mappings
    'email': 'emails',
    'email address': 'emails',
    'emailaddress': 'emails',
    'email1': 'emails',
    'primary email': 'emails',
    'work email': 'emails',
    'personal email': 'emails',
    'e-mail': 'emails',
    'e-mail address': 'emails',
    
    // Phone mappings
    'phone': 'phones',
    'phone number': 'phones',
    'phonenumber': 'phones',
    'telephone': 'phones',
    'tel': 'phones',
    'mobile': 'phones',
    'cell': 'phones',
    'work phone': 'phones',
    'home phone': 'phones',
    'mobile phone': 'phones',
    'cell phone': 'phones',
    'fax': 'phones',

    // Job title (the .vcf path maps TITLE -> job_title)
    'job title': 'job_title',
    'jobtitle': 'job_title',
    // ponytail: no bare 'title' (Outlook's honorific column) or 'position' (word fallback: "Position Size").

    // Birthday (the .vcf path writes BDAY to dates with label 'birthday')
    'birthday': 'dates',
    'birth date': 'dates',
    'birthdate': 'dates',
    'date of birth': 'dates',
    'dob': 'dates',
    'anniversary': 'dates',

    // Full address mappings (single field containing entire address)
    'address': 'addresses',
    'full address': 'addresses',
    'mailing address': 'addresses',
    'home address': 'addresses',
    'work address': 'addresses',

    // Address component mappings - Street (Address 1)
    'street': 'address_street',
    'street address': 'address_street',
    'streetaddress': 'address_street',
    'address1': 'address_street',
    'address 1': 'address_street',
    'address line 1': 'address_street',
    'address/line/1': 'address_street',
    'address - line 1': 'address_street',
    'address-line-1': 'address_street',
    'addressline1': 'address_street',
    'street1': 'address_street',
    'street 1': 'address_street',
    'addr': 'address_street',
    'addr1': 'address_street',
    'line1': 'address_street',
    'line 1': 'address_street',
    // Address 1 with prefix variations
    'address 1 street': 'address_street',
    'address 1 line 1': 'address_street',
    'address1 street': 'address_street',
    'primary address': 'address_street',
    'primary address street': 'address_street',
    'mailing street': 'address_street',
    'billing street': 'address_street',
    'billing address street': 'address_street',

    // Address component mappings - Street Line 2 (Address 1)
    'address2': 'address_street2',
    'address 2': 'address_street2',
    'address line 2': 'address_street2',
    'address/line/2': 'address_street2',
    'address - line 2': 'address_street2',
    'address-line-2': 'address_street2',
    'addressline2': 'address_street2',
    'street2': 'address_street2',
    'street 2': 'address_street2',
    'addr2': 'address_street2',
    'line2': 'address_street2',
    'line 2': 'address_street2',
    'apt': 'address_street2',
    'apartment': 'address_street2',
    'suite': 'address_street2',
    'unit': 'address_street2',
    'address 1 line 2': 'address_street2',
    'address1 line 2': 'address_street2',

    // Address component mappings - City (Address 1)
    'city': 'address_city',
    'town': 'address_city',
    'locality': 'address_city',
    'address 1 city': 'address_city',
    'address1 city': 'address_city',
    'billing city': 'address_city',
    'mailing city': 'address_city',

    // Address component mappings - State (Address 1)
    'state': 'address_state',
    'province': 'address_state',
    'region': 'address_state',
    'st': 'address_state',
    'state/province': 'address_state',
    'state / province': 'address_state',
    'state-province': 'address_state',
    'address 1 state': 'address_state',
    'address1 state': 'address_state',
    'billing state': 'address_state',
    'mailing state': 'address_state',

    // Address component mappings - Zip (Address 1)
    'zip': 'address_zip',
    'zipcode': 'address_zip',
    'zip code': 'address_zip',
    'zip/postal code': 'address_zip',
    'zip / postal code': 'address_zip',
    'postal': 'address_zip',
    'postal code': 'address_zip',
    'postalcode': 'address_zip',
    'postcode': 'address_zip',
    'post code': 'address_zip',
    'address 1 zip': 'address_zip',
    'address1 zip': 'address_zip',
    'address 1 postal code': 'address_zip',
    'billing zip': 'address_zip',
    'billing zip code': 'address_zip',
    'mailing zip': 'address_zip',

    // Address component mappings - Country (Address 1)
    'country': 'address_country',
    'nation': 'address_country',
    'country/region': 'address_country',
    'country / region': 'address_country',
    'address 1 country': 'address_country',
    'address1 country': 'address_country',
    'billing country': 'address_country',
    'mailing country': 'address_country',

    // ===== ADDRESS 2 MAPPINGS =====
    // For files with two separate addresses (e.g., billing + shipping)

    // Address 2 - Street
    'address 2 street': 'address2_street',
    'address2 street': 'address2_street',
    'address 2 line 1': 'address2_street',
    'address2 line 1': 'address2_street',
    'secondary address': 'address2_street',
    'secondary address street': 'address2_street',
    'shipping street': 'address2_street',
    'shipping address': 'address2_street',
    'shipping address street': 'address2_street',
    'other address': 'address2_street',
    'alternate address': 'address2_street',

    // Address 2 - Street Line 2
    'address 2 line 2': 'address2_street2',
    'address2 line 2': 'address2_street2',
    'shipping street 2': 'address2_street2',
    'shipping apt': 'address2_street2',

    // Address 2 - City
    'address 2 city': 'address2_city',
    'address2 city': 'address2_city',
    'shipping city': 'address2_city',
    'secondary city': 'address2_city',

    // Address 2 - State
    'address 2 state': 'address2_state',
    'address2 state': 'address2_state',
    'shipping state': 'address2_state',
    'secondary state': 'address2_state',

    // Address 2 - Zip
    'address 2 zip': 'address2_zip',
    'address2 zip': 'address2_zip',
    'address 2 postal code': 'address2_zip',
    'shipping zip': 'address2_zip',
    'shipping zip code': 'address2_zip',
    'secondary zip': 'address2_zip',

    // Address 2 - Country
    'address 2 country': 'address2_country',
    'address2 country': 'address2_country',
    'shipping country': 'address2_country',
    'secondary country': 'address2_country',
    
    // Company mappings
    'company': 'company',
    'company name': 'company',
    'companyname': 'company',
    'organization': 'company',
    'org': 'company',
    'business': 'company',
    'employer': 'company',
    
    // Website mappings
    'website': 'website',
    'web': 'website',
    'url': 'website',
    'site': 'website',
    'web address': 'website',
    'homepage': 'website',
    
    // Notes mappings
    'notes': 'notes',
    'note': 'notes',
    'comments': 'notes',
    'comment': 'notes',
    'description': 'notes',
    'desc': 'notes',
    'remarks': 'notes',
    'remark': 'notes',
  }

  // Helper function to normalize column names for matching
  // Handles various separators: slashes, dashes, underscores, etc.
  const normalizeColumnName = (column: string): string => {
    return column
      .toLowerCase()
      .trim()
      // Replace common separators with spaces
      .replace(/[\/\-_]+/g, ' ')
      // Collapse multiple spaces into one
      .replace(/\s+/g, ' ')
      .trim()
  }

  // T7: Auto map CSV columns based on common naming patterns with smart defaults
  const autoMapColumns = (columns: string[]) => {
    const mappings: { [key: string]: { field: string; label?: string; isPrimary?: boolean } } = {}

    // Track counts for each multi-value field type to apply smart defaults
    const fieldCounts: { [key: string]: number } = {
      emails: 0,
      phones: 0,
      addresses: 0,
      dates: 0,
    }

    columns.forEach(column => {
      // Normalize column name for comparison
      const normalizedColumn = normalizeColumnName(column)

      let matchedField: string | null = null

      // Check if this column name matches any of our known patterns
      if (commonColumnMappings[normalizedColumn]) {
        matchedField = commonColumnMappings[normalizedColumn]
      } else {
        // Try matching without the normalization (exact lowercase match)
        const basicNormalized = column.toLowerCase().trim()
        if (commonColumnMappings[basicNormalized]) {
          matchedField = commonColumnMappings[basicNormalized]
        } else {
          // For multi-word columns, check if any part matches
          const words = normalizedColumn.split(/\s+/)
          for (const word of words) {
            if (commonColumnMappings[word]) {
              matchedField = commonColumnMappings[word]
              break
            }
          }
        }
      }

      if (matchedField) {
        const mapping: { field: string; label?: string; isPrimary?: boolean } = { field: matchedField }

        // T7: Apply smart label defaults for multi-value fields
        if (matchedField === 'emails') {
          if (fieldCounts.emails === 0) {
            // First email: default label = "work", mark as primary
            mapping.label = 'work'
            mapping.isPrimary = true
          } else {
            // Additional emails: default label = "additional"
            mapping.label = 'additional'
          }
          fieldCounts.emails++
        } else if (matchedField === 'phones') {
          if (fieldCounts.phones === 0) {
            // First phone: default label = "main", mark as primary
            mapping.label = 'main'
            mapping.isPrimary = true
          } else {
            // Additional phones: default label = "additional"
            mapping.label = 'additional'
          }
          fieldCounts.phones++
        } else if (matchedField === 'addresses') {
          if (fieldCounts.addresses === 0) {
            // First address: default label = "main", mark as primary
            mapping.label = 'main'
            mapping.isPrimary = true
          } else {
            // Additional addresses: default label = "additional"
            mapping.label = 'additional'
          }
          fieldCounts.addresses++
        } else if (matchedField === 'dates') {
          if (fieldCounts.dates === 0) {
            // First date: default label = "birthday" (common case)
            mapping.label = 'birthday'
            mapping.isPrimary = true
          } else {
            // Additional dates: default label = "additional"
            mapping.label = 'additional'
          }
          fieldCounts.dates++
        }

        // A label word in the header (Work/Personal/Mobile/Fax/Birthday...) beats the position default.
        // Primary stays position-based (first column of its kind).
        const headerLabel = labelFromHeader(normalizedColumn, matchedField)
        if (headerLabel) mapping.label = headerLabel

        mappings[column] = mapping
      }
    })

    return mappings
  }

  // T7: Auto map vCard columns with smart defaults
  // Note: email, tel, adr, bday are auto-handled with per-entry labels from meta.type
  // so they're not included in mappings here (they're in autoMappedVCardFields)
  const autoMapVcfColumns = (vcfColumns: string[]) => {
    const mappings: { [key: string]: { field: string; label?: string; isPrimary?: boolean } } = {}
    const vcfFieldMap: { [key: string]: string } = {
      org: 'company',
      title: 'job_title',
      url: 'website',
      note: 'notes',
    }

    vcfColumns.forEach((column) => {
      const lowerColumn = column.toLowerCase()
      if (vcfFieldMap[lowerColumn]) {
        mappings[column] = { field: vcfFieldMap[lowerColumn] }
      }
    })

    return mappings
  }

  const onDrop = (acceptedFiles: File[]) => {
    const selectedFile = acceptedFiles[0]
    if (selectedFile) {
      setFile(selectedFile)
      if (isCsvFile(selectedFile)) {
        Papa.parse(selectedFile, {
          header: true,
          skipEmptyLines: true,
          preview: 1,
          complete: (results) => {
            const detectedColumns = results.meta.fields || []
            setColumns(detectedColumns)

            // Check if this is a CRM export format
            const isCrm = detectCrmSchema(detectedColumns)
            setIsCrmSchema(isCrm)

            if (isCrm) {
              // CRM schema detected - skip mapping and go directly to preview
              // Set up column map for preview display (subset shown in table)
              const crmColumnMap: { [key: string]: { field: string; label?: string; isPrimary?: boolean } } = {
                first_name: { field: 'first_name' },
                last_name: { field: 'last_name' },
                company: { field: 'company' },
                job_title: { field: 'job_title' },
                primary_email: { field: 'emails', label: 'work', isPrimary: true },
                primary_phone: { field: 'phones', label: 'work', isPrimary: true },
                type: { field: 'type' },
                rank: { field: 'rank' },
                status: { field: 'status' },
              }
              setColumnMap(crmColumnMap)

              // Parse the full file for preview
              Papa.parse(selectedFile, {
                header: true,
                skipEmptyLines: true,
                complete: (fullResults) => {
                  const data = fullResults.data as Record<string, string>[]
                  setCSVData(data)

                  // Count non-empty JSON array fields across all rows for summary
                  const fieldCounts: Record<string, number> = {}
                  const jsonFields = ['emails', 'phones', 'addresses', 'urls', 'notes', 'groups', 'interests', 'connections', 'custom_fields', 'important_dates']
                  data.forEach((row) => {
                    jsonFields.forEach((field) => {
                      const val = row[field]?.trim()
                      if (val && val !== '[]' && val !== '') {
                        fieldCounts[field] = (fieldCounts[field] || 0) + 1
                      }
                    })
                  })
                  // Store field counts for display in preview
                  setCrmFieldCounts(fieldCounts)

                  // Generate preview using CRM parsers
                  const preview = data.slice(0, 5).map((row) => {
                    const crmContact = parseCrmCsvRow(row)
                    return {
                      first_name: crmContact.first_name,
                      last_name: crmContact.last_name,
                      company: crmContact.company,
                      job_title: crmContact.job_title,
                      primary_email: crmContact.primary_email,
                      primary_phone: crmContact.primary_phone,
                      type: crmContact.type,
                      rank: crmContact.rank,
                      status: crmContact.status,
                    }
                  })
                  setPreviewData(preview)
                  setActiveTab('preview')
                },
              })
            } else {
              // Auto-map the columns for non-CRM files
              const mappings = autoMapColumns(detectedColumns)
              setColumnMap(mappings)
            }
          },
        })
      } else if (selectedFile.type.includes('vcard') || selectedFile.name.toLowerCase().endsWith('.vcf')) {
        const reader = new FileReader()
        reader.onload = (event) => {
          const content = event.target?.result as string
          setFileContent(content)
          // Split into individual vCard blocks. Filter out empty strings.
          const vCardStrings = content.split('BEGIN:VCARD').filter(s => s.trim() !== '')

          if (vCardStrings.length === 0) {
            toast({ title: 'Error', description: 'VCF file appears to be empty or invalid.', variant: 'destructive' })
            return
          }

          // Parse each vCard string into a vCard object
          const parsedVCFs = vCardStrings.map((vCardString) => {
            // Re-add the BEGIN:VCARD that was removed by split()
            const fullVCard = 'BEGIN:VCARD\n' + vCardString
            try {
              return parseVCard(fullVCard)
            } catch (e) {
              console.error('Failed to parse vCard string:', e)
              return null // Handle parsing errors for individual cards
            }
          }).filter(Boolean) as any[] // Filter out nulls from failed parsing

          if (parsedVCFs.length === 0) {
            toast({ title: 'Error', description: 'Could not parse any contacts from the VCF file.', variant: 'destructive' })
            return
          }
          
          setVcfData(parsedVCFs)

          // Define which VCard fields are relevant for contact mapping
          const relevantVCardFields = new Set([
            'fn',      // Full name
            'n',       // Name components (first, last, middle, etc.)
            'email',   // Email address
            'tel',     // Telephone number
            'adr',     // Address
            'org',     // Organization/Company
            'title',   // Job title
            'url',     // Website URL
            'note',    // Notes
            'bday',    // Birthday
          ])

          // Define technical/metadata fields that should be completely hidden
          const technicalFields = new Set([
            'version',    // VCard version
            'prodid',     // Product identifier
            'rev',        // Revision timestamp
            'uid',        // Unique identifier
            'kind',       // Kind of object
            'photo',      // Photo data (usually base64)
            'logo',       // Logo data
            'sound',      // Sound data
            'key',        // Security key
            'categories', // Categories
            'class',      // Classification
            'member',     // Group member
            'related',    // Related contact
            'caluri',     // Calendar URI
            'caladruri',  // Calendar address URI
            'fburl',      // Free/busy URL
            'impp',       // Instant messaging
            'lang',       // Language
            'tz',         // Timezone
            'geo',        // Geographic position
            'source',     // Source
            'xml',        // XML data
            'clientpidmap', // Client PID map
            'vnd-63-sensitive-content-config', // Vendor-specific field
          ])

          // Define Apple/vendor-specific extension fields to hide
          const vendorExtensionFields = new Set([
            'x-ablabel',        // Apple Address Book label
            'x-abdate',         // Apple Address Book date
            'x-abadr',          // Apple Address Book address
            'x-abshowas',       // Apple Address Book display setting
            'x-abuid',          // Apple Address Book UID
            'x-socialprofile',  // Social media profile
            'x-phonetic-first-name', // Phonetic names
            'x-phonetic-last-name',
            'x-phonetic-middle-name',
            'x-maiden-name',    // Maiden name
            'x-aim',            // AIM handle
            'x-icq',            // ICQ handle
            'x-jabber',         // Jabber handle
            'x-msn',            // MSN handle
            'x-yahoo',          // Yahoo handle
            'x-skype',          // Skype handle
          ])

          // Define field priorities to show most useful fields first
          const fieldPriority: { [key: string]: number } = {
            'fn': 1,     // Full name - highest priority
            'n': 2,      // Name components
            'email': 3,  // Email
            'tel': 4,    // Phone
            'org': 5,    // Company
            'title': 6,  // Job title
            'adr': 7,    // Address
            'url': 8,    // Website
            'note': 9,   // Notes
            'bday': 10   // Birthday
          }

          // Extract only relevant field names from all contacts for the mapping UI
          const allKeys = new Set<string>()
          parsedVCFs.forEach((card) => {
            Object.keys(card).forEach((key) => {
              const lowerKey = key.toLowerCase()
              // Only include fields that are relevant and not technical/metadata
              if (relevantVCardFields.has(lowerKey) && 
                  !technicalFields.has(lowerKey) && 
                  !vendorExtensionFields.has(lowerKey) &&
                  !lowerKey.startsWith('x-')) { // Hide any remaining X- extension fields
                allKeys.add(key)
              }
            })
          })
          
          // Sort fields by priority for better user experience
          const detectedColumns = Array.from(allKeys).sort((a, b) => {
            const priorityA = fieldPriority[a.toLowerCase()] || 99
            const priorityB = fieldPriority[b.toLowerCase()] || 99
            return priorityA - priorityB
          })
          
          setColumns(detectedColumns)

          // Auto-map columns based on the detected fields
          const mappings = autoMapVcfColumns(detectedColumns)

          // Add synthetic mappings for auto-handled fields (preview display)
          mappings['__first_name__'] = { field: 'first_name' }
          mappings['__last_name__'] = { field: 'last_name' }
          if (parsedVCFs.some(c => c.email)) mappings['__emails__'] = { field: 'emails' }
          if (parsedVCFs.some(c => c.tel)) mappings['__phones__'] = { field: 'phones' }
          if (parsedVCFs.some(c => c.adr)) mappings['__addresses__'] = { field: 'addresses' }
          if (parsedVCFs.some(c => c.bday)) mappings['__birthday__'] = { field: 'dates' }

          setColumnMap(mappings)

          // Always go to mapFields for vCards — user can review auto-mappings
          // and click "Next" to generate preview data properly
          setActiveTab('mapFields')
        }
        reader.readAsText(selectedFile)
      }
    }
  }

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/csv': ['.csv'],
      'text/vcard': ['.vcf'],
      'text/x-vcard': ['.vcf'],
      'text/directory': ['.vcf'],
      'application/octet-stream': ['.vcf'],
    },
    maxFiles: 1,
  })

  const handleMappingChange = (column: string, field: string, label?: string) => {
    setColumnMap(prev => ({ 
      ...prev, 
      [column]: { 
        ...prev[column], 
        field, 
        label: label || prev[column]?.label 
      } 
    }))
  }

  const handleLabelChange = (column: string, label: string) => {
    setColumnMap(prev => ({ 
      ...prev, 
      [column]: { 
        ...prev[column], 
        label 
      } 
    }))
  }

  const handlePrimaryToggle = (column: string, fieldType: string) => {
    setColumnMap(prev => {
      const newMap = { ...prev }
      
      // First, unset any existing primary for this field type
      Object.keys(newMap).forEach(key => {
        if (newMap[key].field === fieldType) {
          newMap[key] = { ...newMap[key], isPrimary: false }
        }
      })
      
      // Then set this column as primary
      newMap[column] = { 
        ...newMap[column], 
        isPrimary: true 
      }
      
      return newMap
    })
  }

  const handleNextToMap = () => {
    if (file) {
      // For CRM schema, skip mapping and go directly to preview
      if (isCrmSchema) {
        setActiveTab('preview')
      } else if (file.type.includes('vcard') || file.name.toLowerCase().endsWith('.vcf')) {
        // Check if all vCard fields are auto-handled (no user-mappable fields remain)
        const userMappableFields = columns.filter(col =>
          !autoMappedVCardFields.includes(col.toLowerCase())
        )
        if (userMappableFields.length === 0) {
          // Skip mapping, go directly to preview
          handleNextToPreview()
        } else {
          setActiveTab('mapFields')
        }
      } else {
        setActiveTab('mapFields')
      }
    }
  }

  const handleStartImport = async () => {
    if (!file || !targetWorkspaceId) return

    // Immediately show submitting state
    setImportStatus('submitting')

    let parsedData: any[] = []

    if (isCrmSchema && isCsvFile(file)) {
      // Use CRM parsers for CRM schema CSV files
      parsedData = csvData.map((row) => {
        const crmContact = parseCrmCsvRow(row)
        return transformCrmContactToImport(crmContact)
      })
    } else if (isCsvFile(file)) {
      parsedData = csvData.map((row) => {
        const contact: any = { emails: [], phones: [], addresses: [], dates: [], notes: [] }
        // Support multiple addresses (address1 and address2)
        const address1: any = {}
        const address2: any = {}
        Object.entries(columnMap).forEach(([column, mapping]) => {
          if (mapping.field && mapping.field !== 'ignore' && row[column]) {
            const value = decodeUnicodeEscapes(row[column])
            if (mapping.field === 'emails') {
              contact.emails.push({
                email: value,
                label: mapping.label || 'additional',
                is_primary: mapping.isPrimary || false
              })
            }
            else if (mapping.field === 'phones') {
              contact.phones.push({
                phone: value,
                label: mapping.label || 'additional',
                is_primary: mapping.isPrimary || false
              })
            }
            else if (mapping.field === 'dates') {
              const date = normalizeCsvDate(value)
              if (date) {
                contact.dates.push({
                  date,
                  label: mapping.label || 'additional',
                  is_primary: mapping.isPrimary || false
                })
              }
            }
            else if (mapping.field === 'notes') {
              contact.notes.push({ note: value })
            }
            // Address component fields - Address 1
            else if (mapping.field === 'address_street') {
              address1.street = value
              address1.label = mapping.label || 'main'
              address1.is_primary = true
            }
            else if (mapping.field === 'address_street2') {
              address1.street2 = value
            }
            else if (mapping.field === 'address_city') {
              address1.city = value
            }
            else if (mapping.field === 'address_state') {
              address1.state = value
            }
            else if (mapping.field === 'address_zip') {
              address1.zip = value
            }
            else if (mapping.field === 'address_country') {
              address1.country = value
            }
            // Address component fields - Address 2
            else if (mapping.field === 'address2_street') {
              address2.street = value
              address2.label = mapping.label || 'additional'
              address2.is_primary = false
            }
            else if (mapping.field === 'address2_street2') {
              address2.street2 = value
            }
            else if (mapping.field === 'address2_city') {
              address2.city = value
            }
            else if (mapping.field === 'address2_state') {
              address2.state = value
            }
            else if (mapping.field === 'address2_zip') {
              address2.zip = value
            }
            else if (mapping.field === 'address2_country') {
              address2.country = value
            }
            // Full address field (single column contains entire address)
            else if (mapping.field === 'addresses') {
              contact.addresses.push({
                street: value,
                city: '',
                state: '',
                zip: '',
                country: '',
                label: mapping.label || 'additional',
                is_primary: mapping.isPrimary || false
              })
            }
            else {
              contact[mapping.field] = value
            }
          }
        })
        // Add address 1 if any components were mapped
        if (Object.keys(address1).length > 0) {
          contact.addresses.push({
            street: address1.street || '',
            street2: address1.street2 || '',
            city: address1.city || '',
            state: address1.state || '',
            zip: address1.zip || '',
            country: address1.country || '',
            label: address1.label || 'main',
            is_primary: address1.is_primary ?? true
          })
        }
        // Add address 2 if any components were mapped
        if (Object.keys(address2).length > 0) {
          contact.addresses.push({
            street: address2.street || '',
            street2: address2.street2 || '',
            city: address2.city || '',
            state: address2.state || '',
            zip: address2.zip || '',
            country: address2.country || '',
            label: address2.label || 'additional',
            is_primary: false
          })
        }
        return contact
      })
    } else if (file.type.includes('vcard') || file.name.toLowerCase().endsWith('.vcf')) {
      parsedData = vcfData.map((card) => {
        const contact: any = { emails: [], phones: [], addresses: [], dates: [], notes: [] }

        // 1. Auto-handled: Names
        if (card.n) {
          contact.first_name = card.n[0].value[1] || ''
          contact.last_name = card.n[0].value[0] || ''
        } else if (card.fn) {
          const nameParts = card.fn[0].value.split(' ')
          contact.first_name = nameParts[0] || ''
          contact.last_name = nameParts.slice(1).join(' ') || ''
        }

        // 2. Auto-handled: Emails (per-entry labels from meta.type)
        if (card.email && Array.isArray(card.email)) {
          card.email.forEach((e: any, i: number) => {
            const { label, isPrimary } = extractVCardLabel(e.meta, 'emails')
            contact.emails.push({
              email: e.value,
              label,
              is_primary: isPrimary || (i === 0 && !card.email.some((x: any) => x.meta?.type?.map((t: string) => t.toUpperCase()).includes('PREF')))
            })
          })
        }

        // 3. Auto-handled: Phones (per-entry labels from meta.type)
        if (card.tel && Array.isArray(card.tel)) {
          card.tel.forEach((p: any, i: number) => {
            const { label, isPrimary } = extractVCardLabel(p.meta, 'phones')
            contact.phones.push({
              phone: p.value,
              label,
              is_primary: isPrimary || (i === 0 && !card.tel.some((x: any) => x.meta?.type?.map((t: string) => t.toUpperCase()).includes('PREF')))
            })
          })
        }

        // 4. Auto-handled: Addresses (per-entry labels from meta.type)
        // vCard ADR format: [PO Box, Extended, Street, City, State, Zip, Country]
        if (card.adr && Array.isArray(card.adr)) {
          card.adr.forEach((a: any, i: number) => {
            const { label, isPrimary } = extractVCardLabel(a.meta, 'addresses')
            const val = Array.isArray(a.value) ? a.value : []
            contact.addresses.push({
              street: val[2] || '',
              city: val[3] || '',
              state: val[4] || '',
              zip: val[5] || '',
              country: val[6] || '',
              label,
              is_primary: isPrimary || (i === 0 && !card.adr.some((x: any) => x.meta?.type?.map((t: string) => t.toUpperCase()).includes('PREF')))
            })
          })
        }

        // 5. Auto-handled: Birthday
        if (card.bday && Array.isArray(card.bday)) {
          card.bday.forEach((d: any) => {
            contact.dates.push({
              date: d.value,
              label: 'birthday',
              is_primary: true
            })
          })
        }

        // 6. User-mapped fields (org, title, url, note, etc.)
        Object.entries(columnMap).forEach(([column, mapping]) => {
          // Skip synthetic auto-handled columns
          if (column.startsWith('__')) return
          const lowerCaseColumn = column.toLowerCase()
          // Skip auto-handled fields
          if (autoMappedVCardFields.includes(lowerCaseColumn)) return
          if (!mapping.field || mapping.field === 'ignore' || !card[lowerCaseColumn]) return

          const vcfField = card[lowerCaseColumn]
          if (mapping.field === 'notes') {
            contact.notes.push(...vcfField.map((n: any) => ({ note: n.value })))
          } else if (mapping.field === 'company' && vcfField[0]?.value) {
            contact.company = Array.isArray(vcfField[0].value) ? vcfField[0].value[0] : vcfField[0].value
          } else if (vcfField[0]?.value) {
            contact[mapping.field] = vcfField[0].value
          }
        })

        return contact
      })
    }

    // Chunked import. The Supabase edge-function ceiling is ~150s; a single
    // 7k-row payload reliably exceeds it. We split the parsed rows into
    // smaller chunks, send them sequentially with the same importBatchId
    // (returned from the first chunk), and only the final chunk flips the
    // batch row to 'completed'. Each chunk is retried up to MAX_INVOKE_ATTEMPTS
    // times with exponential backoff on transient failures (FunctionsFetchError
    // / 5xx) — a 689-row CSV that lost chunk 2 to a network blip in prod
    // motivated the retry. 4xx responses are not retried since they will not
    // succeed on a second attempt.
    const CHUNK_SIZE = 200
    const MAX_INVOKE_ATTEMPTS = 3
    const totalRows = parsedData.length
    setImportedContactCount(totalRows)
    setImportProgress(0)

    const groupAssignmentPayload =
      groupOption.mode !== 'none'
        ? {
            mode: groupOption.mode,
            groupId: groupOption.groupId,
            groupName: groupOption.groupName,
          }
        : undefined

    const invokeImportChunkWithRetry = async (
      body: Record<string, unknown>,
    ): Promise<{ importBatchId?: string; importedContacts?: number } | null> => {
      let lastError: unknown
      for (let attempt = 1; attempt <= MAX_INVOKE_ATTEMPTS; attempt++) {
        const { data, error } = await supabase.functions.invoke('import-contacts', { body })
        if (!error) return data
        lastError = error
        const status =
          (error as { context?: { response?: { status?: number } } })?.context?.response?.status ??
          (error as { status?: number })?.status
        const isPermanent = typeof status === 'number' && status >= 400 && status < 500
        if (isPermanent || attempt === MAX_INVOKE_ATTEMPTS) break
        await new Promise((resolve) => setTimeout(resolve, 1000 * 2 ** (attempt - 1)))
      }
      throw lastError instanceof Error ? lastError : new Error(String(lastError))
    }

    let batchId: string | undefined
    let cumulativeImported = 0
    try {
      for (let offset = 0; offset < totalRows; offset += CHUNK_SIZE) {
        const chunk = parsedData.slice(offset, offset + CHUNK_SIZE)
        const isLast = offset + CHUNK_SIZE >= totalRows
        const data = await invokeImportChunkWithRetry({
          workspaceId: targetWorkspaceId,
          parsedData: chunk,
          fileType: file.type,
          columnMap,
          // fileContent is only used by the function as a fallback when
          // parsedData is absent; sending it on every chunk would balloon
          // payloads. Send it just on the first chunk for parity with the
          // pre-chunked contract.
          fileContent: offset === 0 ? fileContent : undefined,
          contactCount: chunk.length,
          totalCount: totalRows,
          isLastChunk: isLast,
          importBatchId: batchId,
          // Send groupAssignment on every chunk. The edge function's group
          // resolution is idempotent: 'new' mode looks up by group_name
          // first (reusing the row chunk-0 created), 'existing' mode uses
          // the supplied groupId as-is. Sending only on chunk 0 would
          // leave targetGroupId null on every subsequent invocation and
          // skip the bulk_add_to_group RPC, so only the first chunk's
          // contacts would land in the selected group.
          groupAssignment: groupAssignmentPayload,
        })
        if (!data) throw new Error('Empty response from import-contacts')
        if (!batchId && data.importBatchId) batchId = data.importBatchId
        cumulativeImported = data.importedContacts ?? cumulativeImported
        setImportProgress(cumulativeImported)
      }
      setImportedContactCount(cumulativeImported)
      setImportStatus('submitted')
    } catch (err) {
      captureError(err as Error, { feature: 'csv-import-chunked' })
      const message = err instanceof Error ? err.message : String(err)
      toast({
        title: 'Import failed mid-way',
        description: `${cumulativeImported.toLocaleString()} of ${totalRows.toLocaleString()} contacts landed before the error. Details: ${message}`,
        variant: 'destructive',
      })
      // Reset to idle so the user can fix the issue and retry. The
      // partially-imported batch row remains in the DB so the rows are
      // recoverable; the user can either trim the CSV and re-import the
      // tail, or use the Import History view to inspect and roll back.
      setImportStatus('idle')
    }
  }

  // Handle closing dialog after import is submitted
  const handleCloseAfterSubmit = () => {
    setIsOpen(false)
    resetState()
  }

  const handleNextToPreview = () => {
    if (!file) return

    setLoading(true)

    if (isCsvFile(file)) {
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        complete: (results) => {
          const data = results.data as any[]
          setCSVData(data)
          const preview = data.slice(0, 5).map((row) => {
            const mappedRow: Record<string, any> = {}
            Object.entries(columnMap).forEach(([column, mapping]) => {
              if (mapping.field) {
                mappedRow[column] = row[column]
              }
            })
            return mappedRow
          })
          setPreviewData(preview)
          setActiveTab('preview')
          setLoading(false)
        },
        error: () => {
          toast({ title: 'Error', description: 'Error parsing CSV file', variant: 'destructive' })
          setLoading(false)
        },
      })
    } else if (file.type.includes('vcard') || file.name.toLowerCase().endsWith('.vcf')) {
      const preview = vcfData.slice(0, 5).map((card) => {
        const mappedRow: Record<string, any> = {}

        // Names
        if (card.n && Array.isArray(card.n) && card.n[0]?.value) {
          const nameComponents = card.n[0].value
          if (Array.isArray(nameComponents)) {
            mappedRow['__first_name__'] = nameComponents[1] || ''
            mappedRow['__last_name__'] = nameComponents[0] || ''
          }
        } else if (card.fn && Array.isArray(card.fn) && card.fn[0]?.value) {
          const nameParts = card.fn[0].value.split(' ')
          mappedRow['__first_name__'] = nameParts[0] || ''
          mappedRow['__last_name__'] = nameParts.slice(1).join(' ') || ''
        }

        // Auto-handled: Emails with per-entry labels
        if (card.email && Array.isArray(card.email)) {
          mappedRow['__emails__'] = card.email
            .map((e: any) => {
              const { label } = extractVCardLabel(e.meta, 'emails')
              return `${e.value} (${label})`
            })
            .join('; ')
        }

        // Auto-handled: Phones with per-entry labels
        if (card.tel && Array.isArray(card.tel)) {
          mappedRow['__phones__'] = card.tel
            .map((p: any) => {
              const { label } = extractVCardLabel(p.meta, 'phones')
              return `${p.value} (${label})`
            })
            .join('; ')
        }

        // Auto-handled: Addresses with per-entry labels
        if (card.adr && Array.isArray(card.adr)) {
          mappedRow['__addresses__'] = card.adr
            .map((a: any) => {
              const { label } = extractVCardLabel(a.meta, 'addresses')
              const val = Array.isArray(a.value) ? a.value : []
              const parts = [val[2], val[3], val[4], val[5], val[6]].filter(v => v && v.trim())
              return `${parts.join(', ')} (${label})`
            })
            .join('; ')
        }

        // Auto-handled: Birthday
        if (card.bday && Array.isArray(card.bday)) {
          mappedRow['__birthday__'] = card.bday[0]?.value || ''
        }

        // User-mapped fields (org, title, url, note)
        Object.entries(columnMap).forEach(([column, mapping]) => {
          if (column.startsWith('__')) return
          if (!mapping.field) return
          const lowerColumn = column.toLowerCase()
          if (autoMappedVCardFields.includes(lowerColumn)) return
          if (!card[lowerColumn]) return

          const vcfField = card[lowerColumn]
          if (lowerColumn === 'org' && Array.isArray(vcfField)) {
            const val = vcfField[0]?.value
            mappedRow[column] = Array.isArray(val) ? val[0] : val || ''
          } else if (lowerColumn === 'note' && Array.isArray(vcfField)) {
            mappedRow[column] = vcfField.map((n: any) => n.value || '').filter((v: string) => v).join('; ')
          } else if (Array.isArray(vcfField)) {
            mappedRow[column] = vcfField[0]?.value || ''
          } else {
            mappedRow[column] = vcfField || ''
          }
        })
        return mappedRow
      })
      setPreviewData(preview)
      setActiveTab('preview')
      setLoading(false)
    }
  }

  const isValidMapping = () => {
    return Object.values(columnMap).every((field) => field.field !== '')
  }

  // Sync internal state with external open prop when it changes
  useEffect(() => {
    setIsOpen(open)
  }, [open])
  
  // Reset state when dialog closes
  useEffect(() => {
    if (!isOpen) {
      resetState()
    }
  }, [isOpen])
  
  // Notify parent component when isOpen changes, but avoid the dependency
  // to prevent an infinite update loop
  useEffect(() => {
    // Use a ref to track previous value to avoid unnecessary calls
    const handler = onOpenChange
    handler(isOpen)
    // We intentionally omit onOpenChange from dependencies
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="icon" onClick={() => setIsOpen(true)}>
          <Upload className="h-4 w-4" /> 
        </Button>
      </DialogTrigger>
      {/* max-h + overflow-y-auto: Radix Dialog centers via translate(-50%,-50%),
          so without a height cap any content taller than the viewport spills
          off both edges. The admin-context banner can push the admin flow over
          on smaller viewports. */}
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Import Contacts</DialogTitle>
          <DialogDescription>
            Import your contacts from CSV or vCard files
          </DialogDescription>
        </DialogHeader>
        {adminContext && (
          <div
            className="flex items-start gap-2 rounded-md border border-border bg-muted/50 px-3 py-2 text-sm"
            role="status"
            aria-label="Admin import context"
          >
            <User className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />
            <div className="text-muted-foreground">
              Importing on behalf of{' '}
              <span className="font-medium text-foreground">
                {adminContext.targetUserName}
              </span>{' '}
              into{' '}
              <span className="font-medium text-foreground">
                {adminContext.targetWorkspaceName}
              </span>
              .
            </div>
          </div>
        )}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          {isCrmSchema ? (
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="upload">Upload</TabsTrigger>
              <TabsTrigger value="preview" disabled={!file}>Preview</TabsTrigger>
              <TabsTrigger value="import" disabled={!file}>Import</TabsTrigger>
            </TabsList>
          ) : (
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="upload">Upload</TabsTrigger>
              <TabsTrigger value="mapFields" disabled={!file}>Map Fields</TabsTrigger>
              <TabsTrigger value="preview" disabled={!file || previewData.length === 0}>Preview</TabsTrigger>
              <TabsTrigger value="import" disabled={!isValidMapping() || previewData.length === 0}>Import</TabsTrigger>
            </TabsList>
          )}
          <TabsContent value="upload">
            <div className="grid gap-4 py-4">
              <div
                {...getRootProps()}
                className={`mt-1 flex justify-center rounded-md border-2 border-dashed px-6 pt-5 pb-6 cursor-pointer ${isDragActive ? 'border-primary' : 'border-border'}`}>
                <div className="space-y-1 text-center">
                  <input {...getInputProps()} />
                  <FileText className="mx-auto h-12 w-12 text-muted-foreground" />
                  <div className="flex text-sm text-muted-foreground">
                    <p className="pl-1">{isDragActive ? 'Drop the files here...' : 'Drag & drop a file or click to select'}</p>
                  </div>
                  <p className="text-xs text-muted-foreground">CSV or VCF file</p>
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                Want to check or clean up a vCard first?{' '}
                <a
                  href="https://usebuildit.com/tools/vcard-to-csv"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline hover:no-underline"
                >
                  Convert it to a spreadsheet
                  <span className="sr-only"> (opens in new tab)</span>
                </a>
                , edit it, then import the CSV here.
              </p>
              {file && (
                <div className="flex items-center justify-between rounded-md border p-3">
                  <div className="flex items-center gap-3">
                    <User className="h-5 w-5 text-muted-foreground" />
                    <span className="text-sm font-medium truncate">{file.name}</span>
                  </div>
                </div>
              )}
              <Button onClick={handleNextToMap} disabled={!file} className="mt-4">
                Next <ChevronRight className="h-4 w-4 ml-2" />
              </Button>
            </div>
          </TabsContent>
          <TabsContent value="mapFields">
            <div className="grid gap-4 py-4">
              <p className="text-sm text-muted-foreground">Map your columns to the correct contact fields. Unmapped columns will be ignored.</p>

              {isCrmSchema && (
                <div className="bg-blue-50 border border-blue-200 rounded-md p-3 mb-4">
                  <p className="text-sm text-blue-800">
                    <strong>CRM Export Format Detected</strong> - Fields will be automatically mapped.
                  </p>
                </div>
              )}

              {/* T3: Mapping Progress Indicator */}
              {(() => {
                const filteredColumns = columns.filter(col => {
                  if (file?.type.includes('vcard')) {
                    return !autoMappedVCardFields.includes(col.toLowerCase())
                  }
                  return true
                })
                const totalFields = filteredColumns.length
                const mappedFields = filteredColumns.filter(col => {
                  const mapping = columnMap[col]
                  return mapping?.field && mapping.field !== '' && mapping.field !== 'ignore'
                }).length
                const progressValue = totalFields > 0 ? (mappedFields / totalFields) * 100 : 0
                const isComplete = mappedFields === totalFields

                return (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className={isComplete ? 'text-green-600 font-medium' : 'text-amber-600'}>
                        {mappedFields} of {totalFields} fields mapped
                      </span>
                    </div>
                    <Progress
                      value={progressValue}
                      className={`h-2 ${isComplete ? '[&>div]:bg-green-500' : '[&>div]:bg-amber-500'}`}
                    />
                  </div>
                )
              })()}

              {/* T1: Column Headers - sticky during scroll */}
              <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-4 bg-muted/50 font-medium text-sm px-4 py-2 rounded-t-lg sticky top-0 z-10">
                <span>Your File</span>
                <span className="w-6"></span>
                <span>Map To</span>
              </div>

              <div className="space-y-4 max-h-60 overflow-y-auto pr-2 -mt-4">
                {file?.type.includes('vcard') && (
                  <div className="bg-blue-50 border border-blue-200 rounded-md p-3 mb-4 mt-4">
                    <p className="text-sm text-blue-800">
                      <strong>Auto-imported from vCard:</strong> Names, email addresses, phone numbers, addresses, and birthdays are automatically imported with their original types/labels. Map the remaining fields below.
                    </p>
                  </div>
                )}
                {columns
                  .filter(col => {
                    // For vCard files, exclude name fields from manual mapping
                    if (file?.type.includes('vcard')) {
                      return !autoMappedVCardFields.includes(col.toLowerCase())
                    }
                    return true
                  })
                  .map((col, index) => {
                    const mapping = columnMap[col]
                    const fieldType = mapping?.field
                    // T5: Only multi-value fields (emails, phones, addresses, dates) show advanced options
                    const isMultiValueField = ['emails', 'phones', 'addresses', 'dates'].includes(fieldType || '')
                    const hasMapping = fieldType && fieldType !== 'ignore' && fieldType !== ''
                    const isIgnored = fieldType === 'ignore'
                    const isExpanded = expandedRows.has(col)
                    const summaryText = isMultiValueField && hasMapping ? getLabelDisplayText(fieldType!, mapping?.label, mapping?.isPrimary) : ''

                    // T4: Determine border color based on mapping status
                    const borderColorClass = hasMapping
                      ? 'border-l-4 border-l-green-500'
                      : isIgnored
                        ? 'border-l-4 border-l-gray-300'
                        : 'border-l-4 border-l-amber-500'

                    return (
                      <div key={index} className={`space-y-3 p-4 border rounded-lg ${borderColorClass}`}>
                        {/* T2: Grid with arrow connector between source and destination */}
                        <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-4">
                          <Label className="truncate font-medium" title={col}>
                            {file?.type.includes('vcard') && vcardFieldDisplayNames[col.toLowerCase()]
                              ? vcardFieldDisplayNames[col.toLowerCase()]
                              : col}
                          </Label>
                          {/* T2: Arrow connector - colored based on mapping state */}
                          <ArrowRight className={`h-5 w-5 flex-shrink-0 ${hasMapping ? 'text-primary' : 'text-muted-foreground'}`} />
                          <Select onValueChange={(value) => handleMappingChange(col, value)} value={mapping?.field || ''}>
                            <SelectTrigger>
                              <SelectValue placeholder="Select a field..." />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="ignore">Ignore this field</SelectItem>
                              {contactFields.map(field => (
                                <SelectItem key={field.value} value={field.value}>{field.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        {/* T5/T6: Collapsible advanced options for multi-value fields only */}
                        {isMultiValueField && hasMapping && (
                          <Collapsible open={isExpanded} onOpenChange={() => toggleRowExpanded(col)}>
                            <div className="flex items-center justify-between ml-4 pl-4 border-l-2 border-muted">
                              <CollapsibleTrigger asChild>
                                <button
                                  type="button"
                                  className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
                                >
                                  {isExpanded ? (
                                    <ChevronUp className="h-4 w-4" />
                                  ) : (
                                    <ChevronDown className="h-4 w-4" />
                                  )}
                                  <span>Options</span>
                                </button>
                              </CollapsibleTrigger>
                              {/* T6: Show summary when collapsed */}
                              {!isExpanded && summaryText && (
                                <span className="text-sm text-muted-foreground">{summaryText}</span>
                              )}
                            </div>
                            <CollapsibleContent className="pt-3">
                              <div className="grid grid-cols-3 items-center gap-4 ml-4 pl-4 border-l-2 border-muted">
                                <Label className="text-sm text-muted-foreground">Label:</Label>
                                <Select onValueChange={(value) => handleLabelChange(col, value)} value={mapping?.label || ''}>
                                  <SelectTrigger>
                                    <SelectValue placeholder="Choose label..." />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {fieldType === 'emails' && emailLabels.map(label => (
                                      <SelectItem key={label.value} value={label.value}>{label.label}</SelectItem>
                                    ))}
                                    {fieldType === 'phones' && phoneLabels.map(label => (
                                      <SelectItem key={label.value} value={label.value}>{label.label}</SelectItem>
                                    ))}
                                    {fieldType === 'addresses' && addressLabels.map(label => (
                                      <SelectItem key={label.value} value={label.value}>{label.label}</SelectItem>
                                    ))}
                                    {fieldType === 'dates' && dateLabels.map(label => (
                                      <SelectItem key={label.value} value={label.value}>{label.label}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                                <Button
                                  variant={mapping?.isPrimary ? "default" : "outline"}
                                  size="sm"
                                  onClick={() => handlePrimaryToggle(col, fieldType!)}
                                  className="h-8"
                                >
                                  <Star className={`h-3 w-3 ${mapping?.isPrimary ? 'fill-current' : ''}`} />
                                  {mapping?.isPrimary ? 'Primary' : 'Set Primary'}
                                </Button>
                              </div>
                            </CollapsibleContent>
                          </Collapsible>
                        )}
                      </div>
                    )
                  })
                }
              </div>
              <Button
                onClick={handleNextToPreview}
                disabled={!file || loading}
                className="mt-4"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Parsing...
                  </>
                ) : (
                  'Preview Data'
                )}
              </Button>
            </div>
          </TabsContent>
          <TabsContent value="preview" className="w-full">
            {isCrmSchema && Object.keys(crmFieldCounts).length > 0 && (
              <div className="bg-blue-50 border border-blue-200 rounded-md p-4 mb-4">
                <p className="text-sm font-semibold text-blue-900 mb-2">CRM Export — Auto-Importing All Fields</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-1 text-sm text-blue-800">
                  {csvData.length > 0 && (
                    <span>{csvData.length} contacts</span>
                  )}
                  {crmFieldCounts.emails > 0 && (
                    <span>{crmFieldCounts.emails} with emails</span>
                  )}
                  {crmFieldCounts.phones > 0 && (
                    <span>{crmFieldCounts.phones} with phones</span>
                  )}
                  {crmFieldCounts.addresses > 0 && (
                    <span>{crmFieldCounts.addresses} with addresses</span>
                  )}
                  {crmFieldCounts.notes > 0 && (
                    <span>{crmFieldCounts.notes} with notes</span>
                  )}
                  {crmFieldCounts.groups > 0 && (
                    <span>{crmFieldCounts.groups} with groups</span>
                  )}
                  {crmFieldCounts.interests > 0 && (
                    <span>{crmFieldCounts.interests} with interests</span>
                  )}
                  {crmFieldCounts.connections > 0 && (
                    <span>{crmFieldCounts.connections} with social links</span>
                  )}
                  {crmFieldCounts.urls > 0 && (
                    <span>{crmFieldCounts.urls} with websites</span>
                  )}
                  {crmFieldCounts.important_dates > 0 && (
                    <span>{crmFieldCounts.important_dates} with dates</span>
                  )}
                  {crmFieldCounts.custom_fields > 0 && (
                    <span>{crmFieldCounts.custom_fields} with custom fields</span>
                  )}
                </div>
              </div>
            )}
            <PreviewTable
              columnMap={columnMap}
              previewData={previewData}
              emailLabels={emailLabels}
              phoneLabels={phoneLabels}
              addressLabels={addressLabels}
              dateLabels={dateLabels}
              onBack={() => setActiveTab(isCrmSchema ? 'upload' : 'mapFields')}
              onNext={() => setActiveTab('import')}
            />
          </TabsContent>
          <TabsContent value="import">
            {importStatus === 'submitted' ? (
              // Confirmation screen after import is submitted
              <div className="flex flex-col items-center gap-6 py-8">
                <div className="rounded-full bg-green-100 p-4">
                  <CheckCircle2 className="h-12 w-12 text-green-600" />
                </div>
                <div className="text-center space-y-2">
                  <h3 className="text-xl font-semibold">Import complete</h3>
                  <p className="text-muted-foreground max-w-md">
                    {importedContactCount.toLocaleString()} contacts imported successfully.
                    {groupOption.mode !== 'none' && ' They have been added to the selected group.'}
                  </p>
                </div>
                <div className="flex gap-3 mt-4">
                  <Button variant="outline" onClick={handleCloseAfterSubmit}>
                    Close
                  </Button>
                  <Button onClick={() => {
                    handleCloseAfterSubmit()
                    // Navigate to import history - use window.location for simplicity
                    window.location.href = '/settings/import-history'
                  }}>
                    <History className="mr-2 h-4 w-4" />
                    View Import History
                  </Button>
                </div>
              </div>
            ) : (
              // Normal import form
              <div className="grid gap-4 py-4">
                <p className="text-sm text-muted-foreground text-center">You're all set!</p>
                <p className="text-center">Click the button below to start the import process.</p>

                <ImportGroupSelector
                  workspaceId={targetWorkspaceId ?? ''}
                  value={groupOption}
                  onChange={setGroupOption}
                  suggestedName={suggestedGroupName}
                />

                {remarketingMapped && (
                  <label className="flex items-start gap-2 text-sm text-muted-foreground cursor-pointer max-w-md mx-auto text-left mt-2">
                    <Checkbox
                      checked={importConsentAffirmed}
                      onCheckedChange={(v) => setImportConsentAffirmed(v === true)}
                      className="mt-0.5"
                    />
                    <span>
                      I confirm I have received permission to send the contacts marked
                      "Allow Marketing = Yes" marketing email.
                    </span>
                  </label>
                )}

                <Button
                  onClick={handleStartImport}
                  disabled={importStatus === 'submitting' || (remarketingMapped && !importConsentAffirmed)}
                  className="mt-4"
                >
                  {importStatus === 'submitting' ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      {importedContactCount > 0
                        ? `Importing... ${importProgress.toLocaleString()} of ${importedContactCount.toLocaleString()}`
                        : 'Importing...'}
                    </>
                  ) : (
                    'Start Import'
                  )}
                </Button>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  )
}